import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { TextCopyButton } from "../../../components/TextCopyButton";
import { Button } from "../../../components/tailwind";
import { truncateHtmlByVisibleWordCount } from "./utils";
import {
  ASSISTANT_MESSAGE_CLASSES,
  GREETING_MESSAGE,
  USER_MESSAGE_CLASSES,
} from "./constants";
import type { Message } from "./types";

export const assistantTextActionTestId = (messageIndex: number): string =>
  `ChatMessageList-useAssistantText-${messageIndex}`;

type ChatMessageListProps = {
  messages: Message[];
  messageStep: number;
  assistantReplyStreaming?: boolean;
  /** When given, assistant messages get an action that hands their text back. */
  onUseAssistantText?: (text: string) => void;
  /** Shown while the conversation is empty. */
  greeting?: Message;
};

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  messageStep,
  assistantReplyStreaming = false,
  onUseAssistantText,
  greeting = GREETING_MESSAGE,
}) => {
  const [revealedLength, setRevealedLength] = useState(0);
  const revealedRef = useRef(0);
  const [lastResponseRender, setLastResponseRender] = useState<Message | null>(
    null
  );

  const lastAssistantText =
    messages.length > 0 && messages[messages.length - 1].role === "assistant"
      ? messages[messages.length - 1].text
      : null;

  useEffect(() => {
    revealedRef.current = revealedLength;
  }, [revealedLength]);

  useEffect(() => {
    if (messageStep === 0) {
      return;
    }

    const last = messages[messages.length - 1];
    if (last == null || last.role !== "assistant") {
      return;
    }

    const words = last.text.split(" ");
    let cancelled = false;

    const animationStep = (revealed: number): void => {
      if (cancelled) {
        return;
      }
      setRevealedLength(revealed);

      if (revealed >= words.length) {
        return;
      }

      window.setTimeout(() => animationStep(revealed + 1), 12);
    };

    animationStep(0);

    return () => {
      cancelled = true;
    };
  }, [messageStep]);

  /* Streaming */
  useEffect(() => {
    if (!assistantReplyStreaming || lastAssistantText == null) {
      return;
    }

    const words = lastAssistantText.split(" ");
    const target = words.length;
    let cancelled = false;

    const animationStep = (revealed: number): void => {
      if (cancelled) {
        return;
      }
      setRevealedLength(revealed);

      if (revealed >= target) {
        return;
      }

      window.setTimeout(() => animationStep(revealed + 1), 12);
    };

    const start = Math.min(revealedRef.current, target);
    animationStep(start);

    return () => {
      cancelled = true;
    };
  }, [assistantReplyStreaming, lastAssistantText]);

  /* Complete message / non-streaming */
  useLayoutEffect(() => {
    if (messageStep === 0) {
      setLastResponseRender(null);
      return;
    }

    const last = messages[messages.length - 1];
    if (last == null || last.role !== "assistant") {
      return;
    }

    setLastResponseRender(last);
    setRevealedLength(0);
  }, [messageStep]);

  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;

  const animateCompleteResponse =
    lastMessage != null &&
    lastMessage.role === "assistant" &&
    messageStep > 0 &&
    lastResponseRender != null &&
    lastMessage.text === lastResponseRender.text;

  const animateStreamedAssistant =
    assistantReplyStreaming &&
    lastMessage != null &&
    lastMessage.role === "assistant";

  const useAnimatedLastMessage =
    animateCompleteResponse || animateStreamedAssistant;

  const renderAnimatedResponse = (
    message: Message,
    index: number,
    revealedWordCount: number
  ): React.JSX.Element => {
    const words = message.text.split(" ");
    let revealedRawText = "";

    for (let i = 0; i < Math.min(revealedWordCount, words.length); i++) {
      revealedRawText += words[i];
      if (i !== words.length - 1) {
        revealedRawText += " ";
      }
    }

    const fullHtml = message.html ?? "";
    const animationComplete = revealedWordCount >= words.length;
    const innerHtml =
      fullHtml && animationComplete
        ? fullHtml
        : fullHtml
        ? truncateHtmlByVisibleWordCount(fullHtml, revealedWordCount)
        : null;

    return (
      <li
        key={index}
        className={[
          "tw-w-full",
          "tw-pb-4",
          "tw-flex",
          "tw-flex-col",
          "tw-gap-2",
          "tw-items-start",
        ].join(" ")}
      >
        {innerHtml != null ? (
          <div
            className={ASSISTANT_MESSAGE_CLASSES}
            dangerouslySetInnerHTML={{ __html: innerHtml }}
          />
        ) : (
          <div className={ASSISTANT_MESSAGE_CLASSES}>{revealedRawText}</div>
        )}

        {message.imageURLs?.map((url) => (
          <img
            key={url.substring(0, 10)}
            className="tw-rounded-md tw-border-solid tw-border"
            src={url}
            alt=""
          />
        ))}
      </li>
    );
  };

  const renderChatMessage = (
    message: Message,
    index: number,
    { allowUseText = true }: { allowUseText?: boolean } = {}
  ): React.JSX.Element => {
    const showUseTextAction =
      allowUseText &&
      onUseAssistantText != null &&
      message.role === "assistant" &&
      message.text.trim().length > 0;

    const copyButton = (
      <TextCopyButton
        data-balloon="copy text"
        className={[
          "tw-transition-all",
          "tw-mx-3",
          "tw-opacity-75",
          "hover:tw-opacity-100",
        ].join(" ")}
        text={message.text}
      />
    );

    return (
      <li
        key={index}
        className={[
          "tw-w-full",
          "tw-flex",
          "tw-flex-col",
          "tw-gap-2",
          "tw-pb-4",
          message.role == "assistant" ? "tw-items-start" : "tw-items-end",
        ].join(" ")}
      >
        {message.role == "assistant" ? (
          <div
            dangerouslySetInnerHTML={{
              __html: message.html || message.text,
            }}
            className={ASSISTANT_MESSAGE_CLASSES}
          ></div>
        ) : (
          <div className={USER_MESSAGE_CLASSES}>{message.text}</div>
        )}

        {message.role === "user" && message.attachedFiles?.length ? (
          <ul className="tw-mx-3 tw-mt-1 tw-mb-0 tw-pl-0 tw-list-none tw-text-xs tw-text-gray-600 tw-space-y-0.5 tw-max-w-xl tw-text-right">
            {message.attachedFiles.map((file) => (
              <li
                key={file.filename}
                className="tw-truncate"
                title={file.filename}
              >
                {file.filename}
              </li>
            ))}
          </ul>
        ) : null}

        {/* Only wrapped when a caller opts in, so the chat page layout is unchanged. */}
        {showUseTextAction ? (
          <div className="tw-flex tw-items-center tw-gap-2">
            {copyButton}
            <Button
              size="sm"
              variant="secondary"
              compact
              data-testid={assistantTextActionTestId(index)}
              onClick={(): void => onUseAssistantText(message.text)}
            >
              Use this text
            </Button>
          </div>
        ) : (
          copyButton
        )}

        {message.imageURLs?.map((url) => (
          <img
            key={url.substring(0, 24)}
            className={[
              "tw-max-w-xl",
              "tw-min-w-[100px]",
              "tw-rounded-md",
              "tw-border-solid",
              "tw-border",
            ].join(" ")}
            src={url}
            alt=""
          />
        ))}
      </li>
    );
  };

  return (
    <ul className="tw-py-6">
      {messages.length > 0 ? (
        <>
          {messages
            .slice(0, -1)
            .map((message, index) => renderChatMessage(message, index))}
          {lastMessage != null &&
            (useAnimatedLastMessage
              ? renderAnimatedResponse(
                  lastMessage,
                  messages.length - 1,
                  revealedLength
                )
              : renderChatMessage(lastMessage, messages.length - 1))}
        </>
      ) : (
        renderChatMessage(greeting, 0, { allowUseText: false })
      )}
    </ul>
  );
};
